export default function BlogContent() {
  return (
    <section className="py-16 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <article className="prose prose-lg max-w-none">
          <div className="text-gray-700 leading-relaxed space-y-6">
            <p className="text-xl text-gray-600 font-medium">
              The cost of maintaining a high-end workstation and the annual subscriptions for AutoCAD and Bluebeam Revu adds up to thousands per year per seat. For many Vancouver contractors, these tools are only used 50% of the time, meaning half of that money is wasted.
            </p>

            <div className="bg-slate-50 border-l-4 border-teal-500 p-6 my-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center">
                <span className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-money-dollar-circle-line text-white text-xl"></i>
                </span>
                The True Cost of Construction Software
              </h2>
              <p className="text-gray-700 mb-4">
                Let's break down what it actually costs to maintain professional-grade construction technology in-house:
              </p>
              <ul className="space-y-2 text-gray-700">
                <li><strong>AutoCAD Subscription:</strong> $2,310/year per seat (2026 pricing)</li>
                <li><strong>Bluebeam Revu Subscription:</strong> $349/year per seat</li>
                <li><strong>High-Performance Workstation:</strong> $3,000-$4,000 (amortized over 3 years = $1,000-$1,333/year)</li>
                <li><strong>Large-Format Monitor:</strong> $800 (amortized over 5 years = $160/year)</li>
                <li><strong>IT Support & Maintenance:</strong> $500-$1,000/year</li>
                <li><strong>Software Updates & Training:</strong> $300-$500/year</li>
                <li><strong>Cloud Storage & Backup:</strong> $200-$400/year</li>
              </ul>
              <p className="text-gray-700 mt-4 mb-0">
                <strong>Total Annual Cost Per Seat:</strong> $4,819-$6,052/year—and that's before you factor in the salary of the person using these tools.
              </p>
            </div>

            <div className="bg-slate-50 border-l-4 border-teal-500 p-6 my-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center">
                <span className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-pie-chart-line text-white text-xl"></i>
                </span>
                The Utilization Problem
              </h2>
              <p className="text-gray-700 mb-4">
                Here's the uncomfortable truth: most Vancouver contractors only use their AutoCAD and Bluebeam licenses <strong>50-60% of the time</strong>. Why?
              </p>
              <ul className="space-y-2 text-gray-700">
                <li><strong>Project Gaps:</strong> Between projects, your software sits idle while you keep paying</li>
                <li><strong>Seasonal Slowdowns:</strong> BC's rainy season creates natural construction lulls</li>
                <li><strong>Permit Delays:</strong> While waiting for approvals, your licenses are unused</li>
                <li><strong>Employee Downtime:</strong> Vacation, sick days, and training time mean zero productivity</li>
              </ul>
              <p className="text-gray-700 mt-4 mb-0">
                <strong>The Math:</strong> If you're only using your $5,000/year software stack 50% of the time, you're effectively wasting <strong>$2,500/year per seat</strong>. For a firm with 2-3 detailers, that's $5,000-$7,500 in pure waste annually.
              </p>
            </div>

            <div className="bg-slate-50 border-l-4 border-teal-500 p-6 my-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center">
                <span className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-shield-check-line text-white text-xl"></i>
                </span>
                Our Tech, Your Profit
              </h2>
              <p className="text-gray-700 mb-4">
                When you contract with <strong>Dinari Designs</strong>, we provide the tools, the licenses, and the high-speed processing power. Here's what you get:
              </p>
              <ul className="space-y-2 text-gray-700">
                <li><strong>Zero Software Costs:</strong> We maintain our own AutoCAD and Bluebeam licenses</li>
                <li><strong>No Hardware Investment:</strong> We provide high-performance workstations optimized for CAD</li>
                <li><strong>No IT Headaches:</strong> We handle updates, backups, and technical troubleshooting</li>
                <li><strong>Professional Output:</strong> Clean DWG files and indexed PDFs ready for your team</li>
                <li><strong>100% Utilization:</strong> You only pay when we're actively working on your project</li>
                <li><strong>Latest Technology:</strong> We stay current with software updates—you don't pay for upgrades</li>
              </ul>
              <p className="text-gray-700 mt-4 mb-0">
                Let us handle the tech while you handle the concrete. You get professional-grade output without the recurring subscription bills.
              </p>
            </div>

            <div className="bg-slate-50 border-l-4 border-teal-500 p-6 my-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center">
                <span className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-calculator-line text-white text-xl"></i>
                </span>
                The ROI Calculation
              </h2>
              <p className="text-gray-700 mb-4">
                <strong>Scenario:</strong> A Vancouver contractor with 2 in-house detailers
              </p>
              <p className="text-gray-700 mb-4">
                <strong>Annual Software & Hardware Costs:</strong>
              </p>
              <ul className="space-y-2 text-gray-700">
                <li>2 × AutoCAD licenses: $4,620</li>
                <li>2 × Bluebeam licenses: $698</li>
                <li>2 × Workstations (amortized): $2,000</li>
                <li>IT support & misc: $1,500</li>
                <li><strong>Total: $8,818/year</strong></li>
              </ul>
              <p className="text-gray-700 mt-4 mb-4">
                <strong>With Dinari Designs:</strong>
              </p>
              <ul className="space-y-2 text-gray-700">
                <li>Software costs: $0</li>
                <li>Hardware costs: $0</li>
                <li>IT support: $0</li>
                <li><strong>Savings: $8,818/year</strong></li>
              </ul>
              <p className="text-gray-700 mt-4 mb-0">
                Plus, you eliminate the risk of underutilization. Every dollar you spend with us goes directly into productive work—not idle software licenses.
              </p>
            </div>

            <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl p-8 my-12 text-white">
              <h2 className="text-3xl font-bold mb-4">
                Ready to lean out your overhead?
              </h2>
              <p className="text-lg mb-6 text-teal-50">
                Stop paying for licenses, workstations, and IT support. Get professional <strong>AutoCAD</strong> and <strong>Bluebeam</strong> output without the recurring costs—only pay for actual project work.
              </p>
              <a 
                href="/home#contact" 
                className="inline-flex items-center px-8 py-4 bg-white text-teal-600 font-semibold rounded-lg hover:bg-teal-50 transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap"
              >
                Get a Quote
                <i className="ri-arrow-right-line ml-2"></i>
              </a>
            </div>

            <div className="border-t border-gray-200 pt-8 mt-12">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center space-x-4">
                  <span className="text-sm font-medium text-gray-500">Share this article:</span>
                  <div className="flex space-x-3">
                    <a href="#" className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center hover:bg-teal-500 hover:text-white transition-colors cursor-pointer">
                      <i className="ri-linkedin-fill"></i>
                    </a>
                    <a href="#" className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center hover:bg-teal-500 hover:text-white transition-colors cursor-pointer">
                      <i className="ri-twitter-x-fill"></i>
                    </a>
                    <a href="#" className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center hover:bg-teal-500 hover:text-white transition-colors cursor-pointer">
                      <i className="ri-facebook-fill"></i>
                    </a>
                  </div>
                </div>
                <a 
                  href="/knowledge-hub" 
                  className="text-teal-600 hover:text-teal-700 font-medium flex items-center cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line mr-2"></i>
                  Back to Knowledge Hub
                </a>
              </div>
            </div>
          </div>
        </article>
      </div>
    </section>
  );
}
